import React, { useState, useEffect } from "react";
import { Button, Dropdown, DropdownItemProps, Modal } from "semantic-ui-react";
import {
  fetchAllStreamsWithDetails,
  FetchStreamsAPIResponse,
  StreamFieldDetails,
  fetchDeviceFilterOptions,
} from "../../../../BytebeamClient";
import {
  CompositeCondition,
  Condition,
  dropDownOptionsFromArray,
  SessionType,
  SimpleCondition,
  specialCharacterValidation,
} from "../../../../util";
import SqlWhereClauseBuilder from "../../common/SqlWhereClauseBuilder";
import { StyledInput, StyledLabel } from "../actiontypes/ActionTypesModal";
import { ErrorMessage } from "../roles/CreateOrEditRoleModal";
import styled from "styled-components";

const StyledDropdown = styled(Dropdown)`
  & .message {
    color: ${({ theme }) => theme.colors["text"]} !important;
  }
`;

interface CreateSessionTypeModalProps {
  readonly sessionTypesArray: SessionType[];
  readonly sessionType?: SessionType;
  readonly title: string;
  readonly onSubmit: (sessionType: SessionType) => Promise<void>;
  readonly trigger: React.ReactNode;
}

type ErrorMessageType = {
  showValidationMessage: boolean;
  nameErrorMessage: string;
};

export default function CreateSessionTypeModal(
  props: CreateSessionTypeModalProps
) {
  const [open, setOpen] = React.useState(false);
  const [streamOptions, setStreamOptions] = useState<DropdownItemProps[]>([]);
  const [fields, setFields] = useState<{ [key: string]: StreamFieldDetails }>(
    {}
  );
  const [streams, setStreams] = useState<FetchStreamsAPIResponse>({});
  const [errorMessage, setErrorMessage] = useState<ErrorMessageType>({
    showValidationMessage: false,
    nameErrorMessage: "",
  });
  const [deviceIdOptions, setDeviceIdOptions] = useState<
    { key: string; text: string; value: number }[]
  >([]);

  const defaultSessionType = {
    name: "",
    stream: "",
    condition: { operator: "=", value: "", field: "" } as SimpleCondition,
    device_filter: {},
  };

  const [sessionType, setSessionType] = useState<SessionType>(
    props.sessionType || defaultSessionType
  );
  const [buttonDisabled, setButtonDisabled] = useState(true);

  const isSessionNameValidate = (sessionName: string) => {
    const existingSessionType = props.sessionTypesArray.find(
      (session) => session.name.toLowerCase() === sessionName.toLowerCase()
    );
    if (existingSessionType) {
      setErrorMessage({
        showValidationMessage: true,
        nameErrorMessage: "Session with this name already exists",
      });
      return false;
    } else if (
      !specialCharacterValidation(sessionName) &&
      sessionName.length > 0
    ) {
      setErrorMessage({
        showValidationMessage: true,
        nameErrorMessage:
          "Please enter a valid name, special characters not allowed",
      });
      return false;
    } else {
      setErrorMessage({
        showValidationMessage: false,
        nameErrorMessage: "",
      });
      return true;
    }
  };

  const setInputStream = (inputStream: string) => {
    setSessionType((prevSessionType) => ({
      ...prevSessionType,
      stream: inputStream,
      condition: {
        operator: "=",
        value: "",
        field: "",
      } as SimpleCondition,
    }));

    const fields = streams[inputStream].fields;
    setFields(fields);
  };

  const setDeviceFilter = (deviceIds: number[]) => {
    setSessionType((prevSessionType) => {
      if (!prevSessionType) return prevSessionType;

      return {
        ...prevSessionType,
        device_filter: {
          ...(deviceIds.length > 0 ? { id: deviceIds } : {}),
        } as SessionType["device_filter"],
      };
    });
  };

  const handleNameChange = (e) => {
    setSessionType((prevSessionType) => ({
      ...prevSessionType,
      name: e.target.value,
    }));
    isSessionNameValidate(e.target.value);
  };

  const handleStateModalClose = () => {
    setErrorMessage({
      showValidationMessage: false,
      nameErrorMessage: "",
    });
    setSessionType(defaultSessionType);
    setOpen(false);
  };

  const handleSubmit = async () => {
    try {
      await props.onSubmit(sessionType);
    } catch (error) {
      console.error("Error while creating session type", error);
    } finally {
      handleStateModalClose();
    }
  };

  useEffect(() => {
    const isValidCondition = (condition: Condition) => {
      const compositeOperators = ["and", "or"];

      if (compositeOperators.includes(condition.operator)) {
        const conditions = (condition as CompositeCondition).conditions;
        return conditions.every(isValidCondition);
      } else {
        const simpleCondition = condition as SimpleCondition;
        return (
          simpleCondition.field.length > 0 &&
          simpleCondition.value !== undefined &&
          simpleCondition.value !== ""
        );
      }
    };

    const isSessionTypeValid =
      sessionType.name.length > 0 &&
      sessionType.stream.length > 0 &&
      isSessionNameValidate(sessionType.name) &&
      isValidCondition(sessionType.condition);

    setButtonDisabled(!isSessionTypeValid);
  }, [sessionType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fn = async () => {
      const streams = await fetchAllStreamsWithDetails();

      const filteredStreams = Object.fromEntries(
        Object.entries(streams).filter(
          ([streamName]) => streamName.length <= 64
        )
      );

      const streamOptions = Object.keys(filteredStreams);
      setStreamOptions(dropDownOptionsFromArray(streamOptions));
      setStreams(filteredStreams);
    };

    const fetchDeviceIds = async () => {
      try {
        const options = await fetchDeviceFilterOptions({});
        const idOption = options.find((opt) => opt.filterName === "id");

        if (idOption) {
          setDeviceIdOptions(
            idOption.filterValues.map((id) => ({
              key: id,
              text: id,
              value: parseInt(id),
            }))
          );
        }
      } catch (e) {
        console.log("Error fetching device IDs:", e);
      }
    };

    fn();
    fetchDeviceIds();
  }, []);

  return (
    <Modal
      className="dark"
      onClose={() => {
        handleStateModalClose();
      }}
      onOpen={() => setOpen(true)}
      open={open}
      size="small"
      trigger={props.trigger}
    >
      <Modal.Header>{props.title}</Modal.Header>
      <Modal.Description></Modal.Description>
      <Modal.Content>
        <StyledInput labelPosition="left">
          <StyledLabel>Session Name</StyledLabel>
          <input
            placeholder="Enter session name"
            value={sessionType.name}
            onChange={handleNameChange}
          />
        </StyledInput>
        {errorMessage?.showValidationMessage && (
          <ErrorMessage>{errorMessage?.nameErrorMessage}</ErrorMessage>
        )}

        <StyledInput labelPosition="left">
          <StyledLabel>Stream</StyledLabel>

          <StyledDropdown
            fluid
            search
            placeholder="Stream"
            options={streamOptions}
            value={sessionType.stream}
            onChange={(_e, d) => {
              setInputStream(d.value as string);
            }}
            style={{
              padding: "8px 12px",
              border: "none",
            }}
          />
        </StyledInput>

        <StyledInput labelPosition="left">
          <StyledLabel>Device Filter (IDs)</StyledLabel>
          <Dropdown
            fluid
            selection
            search
            multiple
            placeholder="Select Device IDs"
            options={deviceIdOptions || []}
            defaultValue={sessionType.device_filter?.id || []}
            onChange={(_e, d) => {
              setDeviceFilter(d.value as number[]);
            }}
            style={{
              border: "none",
            }}
          />
        </StyledInput>

        <SqlWhereClauseBuilder
          fields={fields}
          condition={sessionType.condition}
          onChange={(condition) => {
            setSessionType((prevSessionType) => ({
              ...prevSessionType,
              condition,
            }));
          }}
          supportNullOperators={false}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button
          secondary
          onClick={() => {
            handleStateModalClose();
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          primary
          disabled={buttonDisabled}
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
